import React, { useState } from "react";
import { Input } from "@/components/ui/input"; // Adjust import path as needed
import { Eye, EyeOff } from "lucide-react"; // Adjust import path as needed

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: boolean;
  errorMessage?: string;
  bgColor?: string;
  showPasswordToggle?: boolean;
  errorIcon?: React.ReactNode;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  error = false,
  errorMessage,
  bgColor = "#f8f8f8",
  showPasswordToggle = false,
  errorIcon,
  type = "text",
  className = "",
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isNumber = type === "number";

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Determine the actual input type
  const inputType = showPasswordToggle
    ? showPassword
      ? "text"
      : "password"
    : type;

  return (
    <div className="w-full">
      <fieldset
        className={`group border rounded-lg px-2 py-0 focus-within:ring-[1.5px] transition-all ${
          error
            ? "border-[#FF383C] focus-within:ring-[#FF383C]"
            : "border-[#828783] hover:border-[#3b3b3b] focus-within:ring-[#0046A7]"
        }`}
      >
        <legend
          className={`group-focus-within:text-[#0046A7] font-[400] px-1 text-[11px] sm:text-[12px] leading-[100%] font-instrument ${
            error
              ? "text-[#FF383C] group-focus-within:text-[#FF383C]"
              : "text-[#010721]"
          }`}
          style={{ backgroundColor: bgColor }}
        >
          {label}
        </legend>

        {showPasswordToggle ? (
          <div className="flex items-center gap-2">
            <Input
              type={inputType}
              className={`text-[#1D1B20] border-0 px-2 pb-3 sm:pb-3.5 md:pb-4 pt-1.5 sm:pt-2 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 text-[13px] sm:text-[14px] md:text-[15px] bg-transparent flex-1 placeholder:text-[#828783] placeholder:align-bottom ${className}`}
              {...props}
            />
            <button
              type="button"
              onClick={toggleShowPassword}
              className={`transition-colors ${
                error ? "text-[#FF383C]" : "text-[#8c8c8c] hover:text-[#49454f]"
              }`}
            >
              {error && errorIcon ? (
                <div>{errorIcon}</div>
              ) : showPassword ? (
                <EyeOff className="w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5" />
              ) : (
                <Eye className="w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5" />
              )}
            </button>
          </div>
        ) : (
          <Input
            type={inputType}
            className={`text-[#1D1B20] border-0 px-2 pb-3 sm:pb-3.5 md:pb-4 pt-1.5 sm:pt-2 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 text-[13px] sm:text-[14px] md:text-[15px] bg-transparent placeholder:text-[#828783] placeholder:${className}`}
            {...props}
            {...props}
            {...(isNumber && {
              min: 0,
              step: "any",
              onKeyDown: (e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault();
                }
              },
              onPaste: (e) => {
                const paste = e.clipboardData.getData("text");
                if (paste.includes("-") || paste.toLowerCase().includes("e")) {
                  e.preventDefault();
                }
              },
            })}
          />
        )}
      </fieldset>
      {error && errorMessage && (
        <p className="text-[11px] sm:text-[12px] text-[#FF383C] pl-3 mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default InputField;
